import { Link } from "react-router-dom";
import { ArrowRight, Clock, FileText, Search, ShieldCheck, ThumbsUp } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

const STEPS = [
  {
    n: "1",
    title: "Invia la tua richiesta",
    text: "Compila il form descrivendo brevemente la pratica o il servizio di cui hai bisogno. Servono solo pochi minuti: ci sono solo i campi indispensabili.",
  },
  {
    n: "2",
    title: "Prima valutazione gratuita",
    text: "Esaminiamo la richiesta e facciamo insieme una prima valutazione, senza costi e senza impegno.",
  },
  {
    n: "3",
    title: "Ti indichiamo come procedere",
    text: "Ti ricontattiamo rapidamente, definiamo ciò che occorre e, se necessario, predisponiamo la proposta per l’attività da svolgere.",
  },
];

export default function ComeFunziona() {
  return (
    <>
      <Seo
        title="Come funziona"
        description="Come funziona la richiesta di valutazione gratuita di DN STUDIO TECNICO: invii la richiesta, la esaminiamo entro 24 ore e ti indichiamo come procedere. Nessun impegno, nessun pagamento online."
        path="/come-funziona"
      />
      <section className="bg-paper pb-12 pt-14 lg:pb-16 lg:pt-20" data-testid="page-come-funziona">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Come funziona</p>
            <h1 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-antracite sm:text-4xl lg:text-5xl">
              Una prima valutazione gratuita, poi decidiamo insieme come procedere.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted">
              Un percorso chiaro e trasparente, pensato per chiunque abbia bisogno di una pratica tecnica senza
              complicarsi la vita.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="container-site">
          <div className="grid gap-10 md:grid-cols-3 md:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="border-t-2 border-line pt-6" data-testid={`come-funziona-step-${s.n}`}>
                  <span className="font-display text-4xl font-extrabold text-salvia">{s.n}</span>
                  <h2 className="mt-3 font-display text-lg font-bold text-antracite">{s.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div
              className="mt-14 inline-flex items-center gap-2.5 rounded-full bg-petrolio-light px-5 py-3 text-sm font-semibold text-petrolio"
              data-testid="come-funziona-badge-24h"
            >
              <Clock size={16} />
              Rispondiamo alle richieste entro 24 ore.
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-14 rounded-3xl border border-line bg-white p-8 sm:p-10" data-testid="come-funziona-dopo">
              <h2 className="font-display text-xl font-bold text-antracite sm:text-2xl">Cosa succede dopo</h2>
              <ul className="mt-6 space-y-4">
                {[
                  {
                    icon: Search,
                    text: "Esaminiamo il caso: se servono chiarimenti, ti facciamo domande puntuali, senza farti perdere tempo.",
                  },
                  {
                    icon: FileText,
                    text: "Se la pratica si può avviare, ti indichiamo la documentazione eventualmente necessaria e i tempi realistici.",
                  },
                  {
                    icon: ShieldCheck,
                    text: "Definiamo insieme la proposta per l’attività, con modalità e costi chiari: sempre fuori dal sito, a voce o per iscritto.",
                  },
                  {
                    icon: ThumbsUp,
                    text: "Nessun pagamento avviene online: l’invio del modulo è una richiesta di contatto, non un conferimento di incarico.",
                  },
                ].map((x) => (
                  <li key={x.text} className="flex items-start gap-4 text-sm leading-relaxed text-antracite-soft sm:text-base">
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-petrolio-light text-petrolio">
                      <x.icon size={17} strokeWidth={1.8} />
                    </span>
                    {x.text}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-petrolio py-14 text-paper">
        <div className="container-site flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-display text-xl font-bold sm:text-2xl">Pronto a iniziare?</h2>
            <p className="mt-1.5 text-sm text-paper/75">La prima valutazione è gratuita e senza impegno.</p>
          </div>
          <Link to="/richiedi-valutazione" data-testid="come-funziona-cta" className="btn-primary bg-salvia text-antracite hover:bg-[#8FAD9F] hover:shadow-none">
            Richiedi una valutazione gratuita <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
